/**
 * D-03 — create-Rx wizard. Steps: pick patient → add medicine lines → notes → review/submit.
 * Per-class validity is auto-set by the backend from the strictest medicine in the lines.
 *
 * Hidden for now behind DOCTOR_RX_CREATION_ENABLED (lib/features.ts); kept intact for when doctors
 * write prescriptions in the app again.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { createPrescription, ensurePatient, getPatientFamily, searchPatients } from "../../api/doctor";
import type { PatientSearchResult } from "../../api/doctor";
import { describeError } from "../../api/errors";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import { RxLineEditor } from "../../components/RxLineEditor";
import { Select } from "../../components/ui/Select";
import { formatDose, rxLinesReady, toRxLineInput, type RxLineDraft } from "../../lib/rxLines";
import { useDebouncedValue } from "../../lib/useDebouncedValue";

export function CreateRx() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // step 1 — patient
  const [patient, setPatient] = useState<PatientSearchResult | null>(null);
  const [familyMemberId, setFamilyMemberId] = useState("");

  // step 2 — lines
  const [lines, setLines] = useState<RxLineDraft[]>([]);

  // step 3 — notes
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  // A retry or double click of the same Rx sends the same key, so it can never be issued twice.
  const submitKey = useIdempotencyKey();

  const submit = useMutation({
    mutationFn: () => {
      const body = {
        patient_account_id: patient!.id,
        for_family_member_id: familyMemberId || null,
        notes: notes.trim() || null,
        lines: lines.map(toRxLineInput),
      };
      return createPrescription(body, submitKey.keyFor(body));
    },
    onSuccess: (rx) => {
      submitKey.reset();
      navigate(`/staff/doctor/prescriptions/${rx.id}`);
    },
    onError: (e) => setError(describeError(e)),
  });

  return (
    <div className="max-w-2xl space-y-5">
      <h2 className="text-lg font-semibold">Write a prescription</h2>
      <Steps step={step} />

      {step === 1 && (
        <PatientStep
          patient={patient}
          familyMemberId={familyMemberId}
          onPickPatient={(p) => {
            setPatient(p);
            setFamilyMemberId("");
          }}
          onPickFamily={setFamilyMemberId}
          onNext={() => setStep(2)}
        />
      )}

      {step === 2 && (
        <LinesStep
          lines={lines}
          setLines={setLines}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
        />
      )}

      {step === 3 && (
        <div className="space-y-4 rounded-xl border bg-white p-5 shadow-xs">
          <h3 className="font-medium">Review & submit</h3>
          <div className="text-sm text-gray-600">
            Patient: <b>{patient?.full_name}</b>
            {familyMemberId && " (for a family member)"}
          </div>
          <ul className="divide-y rounded-lg border">
            {lines.map((l, i) => (
              <li key={i} className="px-3 py-2 text-sm">
                <b>{l.medicine_name}</b> · qty {l.quantity} · {formatDose(l.dosage_value, l.dosage_unit)} · {l.food_relation.replace("_", " ")} · {l.time_of_day.join(", ")}
                {l.duration_days.trim() && ` · ${l.duration_days.trim()} days`}
              </li>
            ))}
          </ul>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-500">Notes (optional)</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-lg border px-3 py-1.5" />
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button onClick={() => setStep(2)} className="rounded-lg border px-4 py-2 text-sm">Back</button>
            <button
              onClick={() => submit.mutate()}
              disabled={submit.isPending || !rxLinesReady(lines)}
              className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50"
            >
              {submit.isPending ? "Submitting…" : "Submit prescription"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Steps({ step }: { step: number }) {
  const labels = ["Patient", "Medicines", "Review"];
  return (
    <div className="flex gap-2 text-xs">
      {labels.map((l, i) => (
        <div key={l} className={`flex items-center gap-1 rounded-full px-3 py-1 ${step === i + 1 ? "bg-brand text-brand-fg" : step > i + 1 ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
          <span>{i + 1}</span> {l}
        </div>
      ))}
    </div>
  );
}

function PatientStep({
  patient,
  familyMemberId,
  onPickPatient,
  onPickFamily,
  onNext,
}: {
  patient: PatientSearchResult | null;
  familyMemberId: string;
  onPickPatient: (p: PatientSearchResult) => void;
  onPickFamily: (id: string) => void;
  onNext: () => void;
}) {
  const [q, setQ] = useState("");
  const term = useDebouncedValue(q.trim());
  const { data: results = [] } = useQuery({
    queryKey: ["patient-search", term],
    queryFn: ({ signal }) => searchPatients(term, signal),
    enabled: term.length >= 2,
  });
  const { data: family = [] } = useQuery({
    queryKey: ["patient-family", patient?.id],
    queryFn: () => getPatientFamily(patient!.id),
    enabled: !!patient,
  });

  const noMatches = term.length >= 2 && results.length === 0;

  return (
    <div className="space-y-4 rounded-xl border bg-white p-5 shadow-xs">
      <h3 className="font-medium">Select patient</h3>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or phone…" className="w-full rounded-lg border px-3 py-1.5" />
      {term.length >= 2 && results.length > 0 && (
        <ul className="max-h-48 divide-y overflow-auto rounded-lg border">
          {results.map((p) => (
            <li key={p.id}>
              <button onClick={() => onPickPatient(p)} className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 ${patient?.id === p.id ? "bg-brand/5" : ""}`}>
                <span>{p.full_name}</span>
                <span className="text-gray-400">{p.phone}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {noMatches && !patient && (
        <NewPatientForm query={q} onCreated={onPickPatient} />
      )}

      {patient && (
        <div className="space-y-2 border-t pt-3">
          <div className="text-sm">Selected: <b>{patient.full_name}</b></div>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-500">For</span>
            <Select
              value={familyMemberId}
              options={[{ value: "", label: "The patient" }, ...family.map((f) => ({ value: f.id, label: `${f.full_name} (${f.relationship})` }))]}
              onChange={onPickFamily}
            />
          </label>
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={onNext} disabled={!patient} className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50">
          Next
        </button>
      </div>
    </div>
  );
}

function NewPatientForm({
  query,
  onCreated,
}: {
  query: string;
  onCreated: (p: PatientSearchResult) => void;
}) {
  // Prefill phone if the search looked like a number, else prefill the name.
  const looksLikePhone = /\d/.test(query) && !/[a-z]/i.test(query);
  const [phone, setPhone] = useState(looksLikePhone ? query.trim() : "");
  const [name, setName] = useState(looksLikePhone ? "" : query.trim());
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => ensurePatient({ phone, full_name: name }),
    onSuccess: (p) => onCreated({ id: p.id, full_name: p.full_name, phone: p.phone }),
    onError: (e) => setError(describeError(e)),
  });

  return (
    <div className="space-y-3 rounded-lg border border-dashed bg-gray-50 p-4">
      <p className="text-sm text-gray-600">
        No patient found. Add a new patient to write this prescription.
      </p>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-500">Phone</span>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="10-digit mobile number"
          inputMode="tel"
          className="rounded-lg border px-3 py-1.5"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-500">Full name (optional)</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Patient name"
          className="rounded-lg border px-3 py-1.5"
        />
      </label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button
        onClick={() => {
          setError(null);
          create.mutate();
        }}
        disabled={create.isPending || phone.trim().length < 10}
        className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50"
      >
        {create.isPending ? "Adding…" : "Add patient & continue"}
      </button>
    </div>
  );
}

function LinesStep({
  lines,
  setLines,
  onBack,
  onNext,
}: {
  lines: RxLineDraft[];
  setLines: (l: RxLineDraft[]) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <div className="space-y-4 rounded-xl border bg-white p-5 shadow-xs">
      <h3 className="font-medium">Add medicines</h3>
      <RxLineEditor lines={lines} setLines={setLines} />
      <div className="flex justify-between">
        <button onClick={onBack} className="rounded-lg border px-4 py-2 text-sm">Back</button>
        <button onClick={onNext} disabled={!rxLinesReady(lines)} className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50">
          Review
        </button>
      </div>
    </div>
  );
}
